import React from 'react';
import { Button, ButtonGroup, Table, Badge, SortableTh, RowActions, ACTIONS_COL_WIDTH } from '@/components/ui';
import type { User } from '../../../types/User';
import { usePermissions } from '../../../hooks/usePermissions';

// Test//

interface UsersTableProps {
  users: User[];
  onRowClick: (user: User) => void;
  onViewHistory?: (user: User) => void;
  onActivate?: (user: User) => void;
  onDeactivate?: (user: User) => void;
  onSeasonChange?: (user: User, season: 'summer' | 'winter') => void;
  season?: 'summer' | 'winter' | null;
  sortBy?: string | null;
  sortDir?: 'asc' | 'desc';
  onSort?: (key: string, dir: 'asc' | 'desc') => void;
}

const UsersTable: React.FC<UsersTableProps> = ({
  users,
  onRowClick,
  onViewHistory,
  onActivate,
  onDeactivate,
  onSeasonChange,
  season = null,
  sortBy = null,
  sortDir = 'asc',
  onSort
}) => {
  const { hasPermission } = usePermissions();
  const canEditUsers = hasPermission('user:edit') || hasPermission('employee:edit');

  const handleRowClick = (user: User, event: React.MouseEvent) => {
    // Don't trigger row click if clicking on buttons
    if ((event.target as HTMLElement).closest('button')) {
      return;
    }
    onRowClick(user);
  };

  // History / activate / deactivate no longer need stopPropagation wrappers — RowActions
  // stops row-click propagation for the whole actions cell.

  const handleSeasonToggle = (event: React.MouseEvent, user: User, newSeason: 'summer' | 'winter') => {
    event.stopPropagation();
    if (onSeasonChange) {
      onSeasonChange(user, newSeason);
    }
  };

  // Get user's active season (default to summer if not set)
  const getUserActiveSeason = (user: User): 'summer' | 'winter' => {
    return (user as any).activeSeason || 'summer';
  };

  return (
    // Needs a horizontal scroll region: 10 columns cannot fit a phone. This was
    // `overflow: visible`, which let the table blow straight out of the viewport.
    <div className="tw-w-full tw-overflow-x-auto">
      <Table hover className="tw-mb-0">
        <thead>
          <tr>
            <SortableTh label="Employee Name" sortKey="name" activeKey={sortBy} direction={sortDir} onSort={onSort} style={{ minWidth: '200px' }} />
            <SortableTh label="Emp #" sortKey="employeeNumber" activeKey={sortBy} direction={sortDir} onSort={onSort} style={{ width: '90px' }} />
            <th style={{ width: '140px' }}>Service Area</th>
            <SortableTh label="Email Address" sortKey="email" activeKey={sortBy} direction={sortDir} onSort={onSort} />
            <SortableTh label="Role" sortKey="role" activeKey={sortBy} direction={sortDir} onSort={onSort} />
            <th>Shift Length</th>
            <SortableTh label="Season" sortKey="activeSeason" activeKey={sortBy} direction={sortDir} onSort={onSort} />
            <SortableTh label="Portal User" sortKey="hasPortalAccess" activeKey={sortBy} direction={sortDir} onSort={onSort} className="tw-text-center" style={{ width: '110px' }} />
            <SortableTh label="Active" sortKey="status" activeKey={sortBy} direction={sortDir} onSort={onSort} className="tw-text-center" style={{ width: '90px' }} />
            <th className="tw-text-center" style={{ width: ACTIONS_COL_WIDTH }}>Actions</th>
          </tr>
        </thead>
        <tbody>
        
          {users.map((user) => (
            <tr 
              key={user.id} 
              onClick={(e) => handleRowClick(user, e)} 
              style={{ cursor: 'pointer' }}
              className={user.status === 'DISABLED' ? 'tw-bg-surface-alt' : ''}
            >
              <td style={{ whiteSpace: 'nowrap' }}>{user.name} {user.lastName}</td>
              <td>{user.employeeNumber || ''}</td>
              <td>
                {user.foremanAreas && user.foremanAreas.length > 0 ? (
                  <div className="tw-flex tw-flex-wrap tw-gap-1">
                    {Array.from(new Set(
                      user.foremanAreas
                        .map(fa => fa.serviceArea?.name || fa.serviceArea?.code || `SA ${fa.serviceAreaId}`)
                        .filter(Boolean)
                    )).map((serviceAreaName, index) => (
                      <Badge key={index} bg="info" text="dark">
                        {serviceAreaName}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <span className="tw-text-content-muted">-</span>
                )}
              </td>
              <td>{user.email}</td>
              <td>
                {user.role?.name ? (
                  <Badge bg="primary">{user.role.name}</Badge>
                ) : (
                  <span className="tw-text-content-muted">-</span>
                )}
              </td>
              <td>
                {(() => {
                  const userActiveSeason = getUserActiveSeason(user);
                  return userActiveSeason === 'summer'
                    ? (user.summerShiftLength != null ? `${Number(user.summerShiftLength).toFixed(2)}h` : '-')
                    : userActiveSeason === 'winter'
                    ? (user.winterShiftLength != null ? `${Number(user.winterShiftLength).toFixed(2)}h` : '-')
                    : '-';
                })()}
              </td>
              <td>
                {canEditUsers && onSeasonChange ? (
                  <ButtonGroup size="sm">
                    <Button
                      variant={getUserActiveSeason(user) === 'summer' ? 'warning' : 'outline-warning'}
                      onClick={(e) => handleSeasonToggle(e, user, 'summer')}
                      style={{
                        fontSize: '0.75rem',
                        padding: '0.25rem 0.5rem',
                        minWidth: '60px'
                      }}
                      title="Set to Summer"
                    >
                      <i className="bi bi-sun"></i>
                    </Button>
                    <Button
                      variant={getUserActiveSeason(user) === 'winter' ? 'primary' : 'outline-primary'}
                      onClick={(e) => handleSeasonToggle(e, user, 'winter')}
                      style={{
                        fontSize: '0.75rem',
                        padding: '0.25rem 0.5rem',
                        minWidth: '60px'
                      }}
                      title="Set to Winter"
                    >
                      <i className="bi bi-snow"></i>
                    </Button>
                  </ButtonGroup>
                ) : (
                  <Badge
                    bg={getUserActiveSeason(user) === 'summer' ? 'warning' : 'primary'}
                    text="white"
                  >
                    <i className={`bi ${getUserActiveSeason(user) === 'summer' ? 'bi-sun' : 'bi-snow'} tw-mr-1`}></i>
                    {getUserActiveSeason(user) === 'summer' ? 'Summer' : 'Winter'}
                  </Badge>
                )}
              </td>
              <td className="tw-text-center">
                {user.hasPortalAccess ? (
                  <i className="bi bi-check-circle-fill tw-text-success" style={{ fontSize: '1.5rem' }}></i>
                ) : (
                  <i className="bi bi-x-circle-fill tw-text-danger" style={{ fontSize: '1.5rem' }}></i>
                )}
              </td>
              <td className="tw-text-center">
                {user.status === 'ACTIVE' ? (
                  <i className="bi bi-check-circle-fill tw-text-success" style={{ fontSize: '1.5rem' }}></i>
                ) : (
                  <i className="bi bi-x-circle-fill tw-text-danger" style={{ fontSize: '1.5rem' }}></i>
                )}
              </td>
              <td className="tw-text-center">
                <RowActions
                  align="left"
                  className="tw-justify-center"
                  actions={[
                    { kind: 'edit', label: 'Edit User', onClick: () => onRowClick(user) },
                    {
                      kind: 'history',
                      label: 'View change history',
                      onClick: () => onViewHistory?.(user),
                      hidden: !onViewHistory,
                    },
                    {
                      kind: 'disable',
                      icon: 'bi-person-slash',
                      label: 'Deactivate User',
                      onClick: () => onDeactivate?.(user),
                      hidden: !hasPermission('user:edit') || user.status !== 'ACTIVE' || !onDeactivate,
                    },
                    {
                      kind: 'enable',
                      icon: 'bi-person-check',
                      label: 'Activate User',
                      onClick: () => onActivate?.(user),
                      hidden: !hasPermission('user:edit') || user.status === 'ACTIVE' || !onActivate,
                    },
                  ]}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {users.length === 0 && (
        <div className="tw-text-center tw-py-4">
          <i className="bi bi-people tw-text-6xl tw-font-light tw-text-content-muted"></i>
          <h5 className="tw-mt-3">No employees found</h5>
          <p className="tw-text-content-muted">
            Try adjusting your search terms or filters.
          </p>
        </div>
      )}
    </div>
  );
};

export default UsersTable;
